import { Controller } from "@hotwired/stimulus"

// Drives the "Nova Requisição" multi-step form.
// - Toggles the branch panel based on the chosen request_type
// - Manages dynamic lists (feature edits, new versions, year changes)
// - Cascades selects from JSON endpoints (region → car → version → year → model;
//   region+car → playlist → feature)
export default class extends Controller {
  static targets = [
    "regionSelect", "typeRadio", "typeHint", "branch", "attachmentsCard",
    "featureEditsList", "newVersionsList", "yearChangesList",
    "featureEditTemplate", "newVersionTemplate", "yearChangeTemplate"
  ]

  static values = {
    carsUrl: String,
    versionsUrl: String,
    yearsUrl: String,
    modelsUrl: String,
    playlistsUrl: String,
    featuresUrl: String
  }

  connect() {
    this.currentType = null
    this.indices = { feature_edits: 0, new_versions: 0, year_changes: 0 }
    this.updateTypeAvailability()
  }

  regionChanged(event) {
    const newValue = event.target.value
    const hasRows = this.element.querySelectorAll("[data-row]").length > 0
    const previous = this.previousRegion || ""

    // Changing the market AFTER rows have been filled in would wipe every
    // cascading select (car/version/model/playlist/feature). Confirm before
    // doing that, and revert the change if the user backs out — that's the
    // bug QA reported: "I forgot the market, picked it, lost all my work".
    if (hasRows && previous && previous !== newValue) {
      const ok = window.confirm(
        "Mudar o mercado vai limpar as seleções de carros, versões e features já preenchidas. Continuar?"
      )
      if (!ok) {
        event.target.value = previous
        return
      }
    }

    this.previousRegion = newValue
    this.updateTypeAvailability()

    // If the user just cleared the market, hide every branch and stop —
    // there's nothing to refresh.
    if (!newValue) {
      this.branchTargets.forEach((b) => b.classList.add("hidden"))
      return
    }

    // Refresh cascading selects in any existing rows.
    this.element.querySelectorAll('[data-cascade="car"]').forEach((sel) => {
      this.loadCars(sel)
      this.clearSelectsDownstream(sel, ["version", "year", "model", "playlist", "feature"])
    })
  }

  // Disables the request-type radios until a market is selected, so the
  // user can't fill out a whole branch and then discover the cascading
  // selects are empty because no region was picked.
  updateTypeAvailability() {
    const hasRegion = !!this.regionSelectTarget.value
    this.previousRegion = this.regionSelectTarget.value

    this.typeRadioTargets.forEach((radio) => {
      radio.disabled = !hasRegion
      const wrapper = radio.closest("label")
      if (!wrapper) return
      wrapper.classList.toggle("opacity-50", !hasRegion)
      wrapper.classList.toggle("cursor-not-allowed", !hasRegion)
      wrapper.classList.toggle("cursor-pointer", hasRegion)
    })

    // Show / hide the inline hint near the type group.
    if (this.hasTypeHintTarget) {
      this.typeHintTarget.classList.toggle("hidden", hasRegion)
    }
  }

  typeChanged(event) {
    const type = event.target.value
    this.currentType = type

    // Show only the matching branch panel.
    this.branchTargets.forEach((b) => {
      b.classList.toggle("hidden", b.dataset.branch !== type)
    })

    // Attachments are useful for every type — surface once a type is picked.
    this.attachmentsCardTarget.hidden = false

    // Seed each branch with its first row so the user isn't staring at an empty card.
    if (type === "content_modification" && this.featureEditsListTarget.children.length === 0) {
      this.addFeatureEdit()
    }
    if (type === "new_version" && this.newVersionsListTarget.children.length === 0) {
      this.addNewVersion()
    }
    if (type === "model_year_change" && this.yearChangesListTarget.children.length === 0) {
      this.addYearChange()
    }
  }

  // ── Row management ────────────────────────────────────────────

  addFeatureEdit() {
    this.appendRow(this.featureEditTemplateTarget, this.featureEditsListTarget, "feature_edits")
  }

  addNewVersion() {
    this.appendRow(this.newVersionTemplateTarget, this.newVersionsListTarget, "new_versions")
  }

  addYearChange() {
    this.appendRow(this.yearChangeTemplateTarget, this.yearChangesListTarget, "year_changes")
  }

  removeRow(event) {
    const row = event.target.closest("[data-row]")
    if (row) row.remove()
  }

  appendRow(template, list, key) {
    const idx = this.indices[key]++
    const html = template.innerHTML.replace(/__IDX__/g, idx)
    list.insertAdjacentHTML("beforeend", html)
    const newRow = list.lastElementChild
    newRow.querySelector("[data-row-label]").textContent = `#${idx + 1}`

    // Seed the car select with cars for the currently chosen region (if any).
    const carSelect = newRow.querySelector('[data-cascade="car"]')
    if (carSelect) this.loadCars(carSelect)
  }

  // ── Cascade handlers ──────────────────────────────────────────

  carChanged(event) {
    const sel = event.target
    this.captureSelectedName(sel)
    this.loadVersions(this.siblingSelect(sel, "version"))
    this.clearSelectsDownstream(sel, ["version", "year", "model", "playlist", "feature"])
    // After picking a car, playlists become loadable too.
    this.loadPlaylists(this.siblingSelect(sel, "playlist"))
  }

  versionChanged(event) {
    const sel = event.target
    this.captureSelectedName(sel)
    this.loadYears(this.siblingSelect(sel, "year"))
    this.clearSelectsDownstream(sel, ["year", "model", "feature"])
  }

  yearChanged(event) {
    const sel = event.target
    this.loadModels(this.siblingSelect(sel, "model"))
    this.clearSelectsDownstream(sel, ["model", "feature"])
  }

  modelChanged(event) {
    const sel = event.target
    this.captureSelectedName(sel)
    this.clearSelectsDownstream(sel, ["feature"])
    this.loadFeatures(this.siblingSelect(sel, "feature"))
  }

  playlistChanged(event) {
    const sel = event.target
    this.captureSelectedName(sel)
    this.loadFeatures(this.siblingSelect(sel, "feature"))
  }

  featureChanged(event) {
    this.captureSelectedName(event.target)
  }

  // ── Cascade fetchers ──────────────────────────────────────────

  async loadCars(carSelect) {
    if (!carSelect) return
    const regionId = this.regionSelectTarget.value
    const url = this.buildUrl(this.carsUrlValue, { region_id: regionId })
    await this.populateSelect(carSelect, url)
  }

  async loadVersions(versionSelect) {
    if (!versionSelect) return
    const carId = this.siblingValue(versionSelect, "car")
    const regionId = this.regionSelectTarget.value
    const url = this.buildUrl(this.versionsUrlValue, { car_id: carId, region_id: regionId })
    await this.populateSelect(versionSelect, url)
  }

  async loadYears(yearSelect) {
    if (!yearSelect) return
    const versionId = this.siblingValue(yearSelect, "version")
    const regionId = this.regionSelectTarget.value
    const url = this.buildUrl(this.yearsUrlValue, { version_id: versionId, region_id: regionId })
    await this.populateSelect(yearSelect, url)
  }

  async loadModels(modelSelect) {
    if (!modelSelect) return
    const versionId = this.siblingValue(modelSelect, "version")
    const year = this.siblingValue(modelSelect, "year")
    const regionId = this.regionSelectTarget.value
    const url = this.buildUrl(this.modelsUrlValue, { version_id: versionId, year: year, region_id: regionId })
    await this.populateSelect(modelSelect, url)
  }

  async loadPlaylists(playlistSelect) {
    if (!playlistSelect) return
    const carId = this.siblingValue(playlistSelect, "car")
    const regionId = this.regionSelectTarget.value
    const url = this.buildUrl(this.playlistsUrlValue, { car_id: carId, region_id: regionId })
    await this.populateSelect(playlistSelect, url)
  }

  async loadFeatures(featureSelect) {
    if (!featureSelect) return
    const carId = this.siblingValue(featureSelect, "car")
    const clusterId = this.siblingValue(featureSelect, "playlist")
    const versionId = this.siblingValue(featureSelect, "version")
    const year = this.siblingValue(featureSelect, "year")
    const modelId = this.siblingValue(featureSelect, "model")
    const regionId = this.regionSelectTarget.value
    if (!carId || !clusterId || !modelId) {
      this.clearFeaturePicker(featureSelect)
      return
    }

    const url = this.buildUrl(this.featuresUrlValue, {
      car_id: carId,
      cluster_id: clusterId,
      version_id: versionId,
      year: year,
      model_id: modelId,
      region_id: regionId
    })
    await this.populateFeaturePicker(featureSelect, url)
  }

  async populateSelect(selectEl, url) {
    if (!selectEl) return
    selectEl.disabled = true
    selectEl.innerHTML = `<option value="">${this.tLoading()}</option>`
    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) throw new Error("fetch failed")
      const items = await resp.json()
      selectEl.innerHTML = `<option value="">${this.tSelect()}</option>` +
        items.map(i => `<option value="${i.id}">${this.escape(i.name)}</option>`).join("")
    } catch (_e) {
      selectEl.innerHTML = `<option value="">${this.tError()}</option>`
    } finally {
      selectEl.disabled = false
    }
  }

  async populateFeaturePicker(hiddenInput, url) {
    if (!hiddenInput) return
    const picker = hiddenInput.closest("[data-feature-picker]")
    if (!picker) return this.populateSelect(hiddenInput, url)

    const query = picker.querySelector('[data-searchable-select-target~="query"]')
    const dropdown = picker.querySelector('[data-searchable-select-target~="dropdown"]')
    const empty = picker.querySelector('[data-searchable-select-target~="empty"]')
    if (!query || !dropdown) return
    if (empty && !empty.dataset.defaultText) empty.dataset.defaultText = empty.textContent

    this.clearFeaturePicker(hiddenInput)
    hiddenInput.disabled = true
    query.disabled = true
    query.value = this.tLoading()

    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) throw new Error("fetch failed")
      const items = await resp.json()

      items.forEach((item) => {
        const label = String(item.name || item.slug || "")
        const slug = String(item.slug || "")
        const option = document.createElement("li")
        option.className = "px-3 py-2 text-sm cursor-pointer hover:bg-primary/10"
        option.dataset.searchableSelectTarget = "option"
        option.dataset.action = "mousedown->searchable-select#select"
        option.dataset.value = item.id
        option.dataset.label = label
        option.dataset.search = `${label} ${slug}`.toLowerCase()
        option.innerHTML = `<span>${this.escape(label)}</span>${slug && slug !== label ? ` <span class="text-xs text-base-content/40 ml-1">${this.escape(slug)}</span>` : ""}`
        dropdown.insertBefore(option, empty || null)
      })

      if (empty) empty.hidden = items.length > 0
    } catch (_e) {
      if (empty) {
        empty.textContent = this.tError()
        empty.hidden = false
      }
    } finally {
      query.value = ""
      query.disabled = false
      hiddenInput.disabled = false
    }
  }

  // ── Helpers ───────────────────────────────────────────────────

  captureSelectedName(sel) {
    const row = sel.closest("[data-row]")
    if (!row) return
    const role = sel.dataset.cascade
    const hidden = row.querySelector(`[data-cascade-name="${role}"]`)
    if (!hidden) return
    const opt = sel.options?.[sel.selectedIndex]
    hidden.value = opt ? opt.textContent.trim() : (sel.dataset.label || "")
  }

  siblingSelect(refSelect, role) {
    const row = refSelect.closest("[data-row]")
    return row?.querySelector(`[data-cascade="${role}"]`) || null
  }

  siblingValue(refSelect, role) {
    return this.siblingSelect(refSelect, role)?.value || ""
  }

  clearSelectsDownstream(refSelect, roles) {
    const row = refSelect.closest("[data-row]")
    if (!row) return
    roles.forEach((role) => {
      const sel = row.querySelector(`[data-cascade="${role}"]`)
      if (role === "feature" && sel) {
        this.clearFeaturePicker(sel)
      } else if (sel) {
        sel.innerHTML = `<option value="">${this.tSelect()}</option>`
      }
      const hidden = row.querySelector(`[data-cascade-name="${role}"]`)
      if (hidden) hidden.value = ""
    })
  }

  clearFeaturePicker(hiddenInput) {
    const row = hiddenInput.closest("[data-row]")
    const picker = hiddenInput.closest("[data-feature-picker]")
    hiddenInput.value = ""
    delete hiddenInput.dataset.label
    const hiddenName = row?.querySelector('[data-cascade-name="feature"]')
    if (hiddenName) hiddenName.value = ""
    if (!picker) return

    const query = picker.querySelector('[data-searchable-select-target~="query"]')
    const dropdown = picker.querySelector('[data-searchable-select-target~="dropdown"]')
    const empty = picker.querySelector('[data-searchable-select-target~="empty"]')

    if (query) query.value = ""
    if (dropdown) {
      dropdown.querySelectorAll('[data-searchable-select-target~="option"]').forEach((option) => option.remove())
      dropdown.hidden = true
    }
    if (empty) {
      if (empty.dataset.defaultText) empty.textContent = empty.dataset.defaultText
      empty.hidden = true
    }
  }

  buildUrl(base, params) {
    const url = new URL(base, window.location.origin)
    Object.entries(params).forEach(([k, v]) => {
      if (v) url.searchParams.set(k, v)
    })
    return url.toString()
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  // Tiny built-in strings (kept here to avoid a round-trip for static labels).
  // The visible business copy still goes through Rails i18n.
  tLoading() { return "…" }
  tSelect()  { return "—" }
  tError()   { return "⚠" }
}
